import { SupplierModel } from '@/models/supplier';
import { FilterColumnList } from '@/collections/filter_columns';
import { FilterColumnModel } from '@/models/filter_column';
import { ReportModel } from '@/models/report';

// Mirrors EXPANSION_COLUMNS in expansionReportFetcher.ts — fetchProductsWithOptions
// auto-appends supplier + supplier_id, so we must NOT include them here. The helper
// also mutates the columns array (push), so callers must clone before passing.
export const SUPPLIER_EXPANSION_COLUMNS = [
  { code: 'variant_id',                dataType: 'number', name: 'Variant ID' },
  { code: 'variant_image',             dataType: 'image',  name: 'Image' },
  { code: 'Item[Item Description]',    dataType: 'string', name: 'Product' },
  { code: 'variant',                   dataType: 'string', name: 'Variant' },
  { code: 'product_sku',               dataType: 'string', name: 'SKU' },
  { code: '[inventory_replenishment]', dataType: 'number', name: 'Reorder Quantity' },
  { code: '[unit_cost]',               dataType: 'number', name: 'Unit Cost' },
];

export interface FetchSupplierProductExpansionArgs {
  supplierId: number;
  variantIds: number[];
}

export interface FetchSupplierProductExpansionResult {
  rows: any[];
  report: ReportModel | null;
}

export async function fetchSupplierProductExpansion(
  args: FetchSupplierProductExpansionArgs,
): Promise<FetchSupplierProductExpansionResult> {
  const distinctIds = Array.from(new Set(
    (args.variantIds || []).filter((id) => Number.isFinite(id)),
  ));
  if (distinctIds.length === 0) { return { rows: [], report: null }; }

  const filters = new FilterColumnList();
  const variantFilter = new FilterColumnModel();
  variantFilter.mapData({
    name: 'Variant',
    code: 'variant_id',
    operator: 'in',
    value: distinctIds,
    dataType: 'number',
  });
  filters.add(variantFilter);

  // operator='eq' tells fetchProductsWithOptions to append a supplier_id=[this.id]
  // filter; that's how we scope the fetch to a single supplier. See supplier.ts:907-918.
  const supplier = new SupplierModel({ id: args.supplierId });
  const itemsPerPage = 500;
  const report = await supplier.fetchProductsWithOptions(
    'inventory_replenishment',
    'eq',
    {
      initialFilterColumns: filters,
      pageOptions: { page: 1, itemsPerPage },
      // Clone — helper mutates via .push() for supplier/supplier_id columns.
      columns: [...SUPPLIER_EXPANSION_COLUMNS],
      // On Supplier Products the user hand-picked rows. Disabling the helper's
      // default `[inventory_replenishment] > 0` filter is required or selected
      // variants with no current reorder need would silently drop out. See
      // spec §3 decision 6.
      suggestFutureReorderQuantity: true,
    },
  );

  const rows = typeof (report as any)?.getReportRowsData === 'function'
    ? (report as any).getReportRowsData()
    : [];

  if (rows.length >= itemsPerPage) {
    throw new Error(
      `Expansion returned the first ${itemsPerPage} variants and may have been truncated. `
        + 'Narrow the selection or load variants incrementally.',
    );
  }

  return { rows, report };
}
